import type { Occupancy, PartyAnswer } from '@elara/domain/models/schemas/room-schemas';

import { occupancyKey } from '@elara/domain/models/schemas/room-schemas';

import { formatOccupancy } from './occupancy-utils.js';

/** What the note reads from a room: the room option model satisfies it. */
export interface PricedRoom {
  allRates: ReadonlyArray<{ occupancy?: Occupancy; available?: boolean | null }>;
  search?: { occupancy?: Occupancy };
  /** Why no rate is priced for the searched party, as the hotel answered; see `PartyAnswerSchema`. */
  partyAnswer?: PartyAnswer;
  /** How many of the room Elara's pick books for rates priced for this party; see `RoomOptionModel.roomsToBook`. */
  roomsToBook(occupancy: Occupancy | undefined): number;
}

/**
 * The note above a room's rates when the rates priced for `party` stand in for the searched party's, on web
 * and mobile alike. A family search also prices the adults alone, and two adults' prices on a family trip
 * must never read as the family's (Andaz 5th Avenue, 2026-10-04); saying only that they differed left the
 * traveller to guess why (2026-10-20), so each case says why, and what to do:
 *   - the hotel's answer about the party (`partyAnswer`, written by the search and by a refusal at
 *     checkout): its booking system sent no quote for children, or quoted the room without checking them
 *     — the Elara team confirms it with the hotel; or it left the room out of what it offers the party —
 *     two of the room, or the team checks; a room whose own stated capacity is too small says nothing,
 *     the cards' "Sleeps N" and "Book 2 rooms" being the whole answer;
 *   - no such answer, and the searched party's rates have all sold out since the search;
 *   - a document from before the answer was recorded: the hotel returned no price for the party.
 * Every case ends by saying whose rates these are.
 *
 * Undefined when `party` is the searched party, or when the searched party still has rates (they lead the
 * list, `orderRatesForParty`). A "book two" pick hides only the notes that merely repeat what each rate card
 * says ("Book 2 rooms to accommodate all guests"): the hotel's answer still shows above those cards, because
 * it says why one room will not do and who can check (Sofitel New York, 2026-10-26: every room that needed
 * the note was a "book two" pick, so none of them showed it).
 */
export function roomPricingNotice(room: PricedRoom, party: Occupancy | undefined): string | undefined {
  const searched = room.search?.occupancy;
  if (!party || !searched || occupancyKey(searched) === occupancyKey(party)) return;
  if (!room.partyAnswer && room.roomsToBook(party) > 1) return;
  const searchedRates = room.allRates.filter(
    (r) => r.occupancy && occupancyKey(r.occupancy) === occupancyKey(searched),
  );
  if (searchedRates.some((r) => r.available !== false)) return;
  const searchedParty = formatOccupancy(searched);
  const theseRates = `These are the rates for ${formatOccupancy(party)}.`;
  switch (room.partyAnswer) {
    case 'no_rates':
    case 'unchecked':
      return `The hotel’s booking system doesn’t quote this room for children online. Contact the Elara team to confirm it for your party. ${theseRates}`;
    case 'refused':
      return `The hotel doesn’t offer this room for ${searchedParty}. Book two, or contact the Elara team to check with the hotel. ${theseRates}`;
    // The room's own capacity says it does not take the party: "Sleeps 3" and "Book 2 rooms" on the
    // cards are the whole answer, and there is nothing for the team to confirm.
    case 'too_small':
      return;
    case undefined:
      break;
  }
  if (searchedRates.length > 0) {
    return `This room’s rates for ${searchedParty} have sold out since Elara searched. These are the rates for ${formatOccupancy(party)}, which may not cover everyone.`;
  }
  return `The hotel didn’t return a price for ${searchedParty} in this room, so it may not accommodate everyone. ${theseRates}`;
}
